// The default "posthog-js" entry point bundles the rrweb session recorder (~67 KB gzipped) into the
// main chunk, and ships it to every user even when recording is turned off — which it now is. The
// slim build excludes the recorder, surveys and exception autocapture.
//
// The only PostHog APIs this app uses are capture(), identify() and reset() (see posthog-helpers.ts
// and posthog-identity.tsx), all of which are core and present in the slim build. If session
// recording is ever wanted back, this import has to go back to "posthog-js" first.
import type posthogType from "posthog-js/dist/module.slim.no-external";

import { hasAnalyticsConsent } from "@/lib/analytics-consent";

/* The import above is a TYPE import, so it costs nothing at runtime. The library itself is
   fetched by the dynamic import inside initPostHog, and only once somebody has said yes.
   It used to be a plain import, which put 111KB of analytics SDK into the bundle of every
   page on the site, including the marketing pages, for every visitor: measured 2026-08-25.
   Consent already stopped it RUNNING, so nothing was ever measured without permission, but
   everybody still paid to download it, and a visitor who declines should not be sending a
   request for an analytics library at all. */

let initialized = false;
let instance: typeof posthogType | null = null;
let pending: Promise<void> | null = null;

/**
 * Start PostHog, if and only if the member has said yes.
 *
 * Asynchronous since 2026-08-25 because the library is now fetched on demand. Callers do
 * not need to await it: anything that reports an event goes through `whenReady` below, so
 * an event fired while the library is still downloading is sent when it arrives rather
 * than dropped.
 */
export async function initPostHog(): Promise<void> {
  if (initialized) return;
  if (pending) return pending;
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV !== "production") return;

  /*
   * NOTHING STARTS UNTIL SOMEBODY HAS SAID YES. This used to run on load, and
   * `persistence: "localStorage"` below means starting it writes to the member's
   * device. PECR wants consent before that happens, so the banner asks first
   * and calls initPostHog() again once the answer is yes. Do not move this check
   * below the init call to "keep the funnel clean": that is the whole bug.
   */
  if (!hasAnalyticsConsent()) return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;

  if (!key || !host) return;

  pending = (async () => {
    const posthog = (await import("posthog-js/dist/module.slim.no-external")).default;

    posthog.init(key, {
      api_host: host,

      // Pageviews are captured manually in PageTracker to support Next.js SPA navigation.
      capture_pageview: false,

      // Use localStorage for session persistence — required for Capacitor (iOS/Android)
      // where cookies behave inconsistently inside WebViews.
      persistence: "localStorage",

      // Disable autocapture to keep events clean and intentional.
      // All meaningful events are tracked explicitly via trackEvent().
      autocapture: false,

      // Do not capture performance metrics (can be noisy on native WebViews).
      capture_performance: false,

      // Session recording is off. It ran a full-DOM recorder on the main thread, and the previous
      // config set maskTextSelector: "*", which forces the recorder to walk and mask EVERY text node
      // in the document on every mutation. On the swipe and chat screens that is a direct cause of
      // dropped frames. Sentry was independently running a second recorder at the same time.
      //
      // Product analytics (events via trackEvent, funnels, retention) are unaffected — only the
      // session video is gone. If recordings are wanted back, turn this on WITHOUT maskTextSelector:"*"
      // and confirm Sentry Replay stays off, so only one recorder is ever running.
      disable_session_recording: true,

      // Disable in-page surveys and toolbar in production apps.
      disable_surveys: true,
    });

    instance = posthog;
    initialized = true;
  })();

  return pending;
}

export function getPostHog(): typeof posthogType | null {
  if (typeof window === "undefined") return null;
  return instance;
}

/**
 * Resolves once PostHog is running, or immediately with null if it never will be.
 *
 * Without this, every event fired in the second or so it takes to fetch the library would
 * be silently dropped, including the "app opened" event that fires on the same tick as
 * init. The old code hid that behind a 100ms setTimeout, which was a guess.
 */
export async function whenReady(): Promise<typeof posthogType | null> {
  if (instance) return instance;
  if (pending) {
    await pending;
    return instance;
  }
  return null;
}
